import {
  getDeadlineQuarterEndDate,
  getDeadlineQuarterKey,
  NO_DEADLINE_QUARTER_KEY,
  parseDeadlineQuarterKey,
} from '@griply/utils';
import { useResolveGoalParentChanges } from 'features/goals';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import { TreeSectionHeader } from 'shared/components/ui/tree-section-header';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  TreeItemMove,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { InheritColors } from 'shared/types/inherit-colors';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useDeadlineGoalListTree } from '../../hooks/use-deadline-goal-list-tree';
import { useOpenCreateInList } from '../../hooks/use-open-create-in-list';
import { GroupingGoalListProps } from '../../types';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './deadline-grouping-goal-list.style';

// Neither section is a deadline quarter: no title to parse, no drop target, no root order.
const isStaticSection = (sectionId: ID) =>
  sectionId === ARCHIVED_SECTION_ID || sectionId === COMPLETED_SECTION_ID;

const droppedTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const DeadlineGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  goalChildrenByParentId,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  onUpdateProgress,
  hideArchived,
  hideCompleted,
  rootLink,
  hideLifeArea,
  showParentGoalLabel,
  showParentLifeAreaLabel,
  showParentLabelsForGoals,
  nestSubGoals,
  isProjectList,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();
  const openCreateInList = useOpenCreateInList(isProjectList);

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const resolveGoalParentChanges = useResolveGoalParentChanges();

  const treeItems = useDeadlineGoalListTree(goals, {
    hideArchived,
    hideCompleted,
    goalChildrenByParentId,
    nestSubGoals,
  });

  const sectionTitle = useCallback(
    (sectionId: ID) => {
      if (sectionId === ARCHIVED_SECTION_ID) {
        return t('pages.goals.archived.title');
      }
      if (sectionId === COMPLETED_SECTION_ID) {
        return t('pages.goals.completed.title');
      }
      if (sectionId === NO_DEADLINE_QUARTER_KEY) {
        return t(
          'context-menus.filtering.task.filters.date.presets.no-deadline',
        );
      }
      const parsed = parseDeadlineQuarterKey(sectionId);
      return parsed ? `Q${parsed.quarter} ${parsed.year}` : sectionId;
    },
    [t],
  );

  // Nesting and un-nesting move one parent link; the frozen legacy chain is never rewritten here.
  const _onReorder = (updatedItems: TreeItem[], moves: TreeItemMove[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];
    const sectionIds = Object.keys(treeItems).filter((id) => !goalsMap[id]);
    const goalParentChanges = resolveGoalParentChanges(moves, sectionIds);
    const movesById = new Map(
      moves
        .filter(({ fromParentId, toParentId }) => fromParentId !== toParentId)
        .map((move) => [move.itemId, move]),
    );

    updatedItems.forEach((item) => {
      if (item.type !== TaskListItemTypes.Goal || !goalsMap[item.id]) {
        return;
      }

      const goalToUpdate: UpdateGoal = {
        id: item.id,
        goalSorting: item.children ?? null,
      };
      const move = movesById.get(item.id);
      const targetGoal = move ? goalsMap[move.toParentId] : undefined;

      if (targetGoal) {
        goalToUpdate.color =
          goalsMap[item.id].color === InheritColors.LifeArea
            ? InheritColors.Goal
            : goalsMap[item.id].color;
        updatedDocs.push(goalToUpdate);
        return;
      }

      // root goal dropped in a quarter section → reschedule the deadline to match
      const sectionKey = move?.toParentId;
      if (sectionKey && !isStaticSection(sectionKey)) {
        const currentKey = getDeadlineQuarterKey(goalsMap[item.id].deadline);
        if (sectionKey !== currentKey) {
          goalToUpdate.deadline =
            sectionKey === NO_DEADLINE_QUARTER_KEY
              ? null
              : getDeadlineQuarterEndDate(sectionKey);
        }
      }

      updatedDocs.push(goalToUpdate);
    });

    // Persist the flat root-goal order (across all quarter sections) as the
    // roadmap custom order — useGoalOverviewReorder routes rootId → goalRoadmapSorting.
    const merged: Record<ID, TreeItem> = { ...treeItems };
    updatedItems.forEach((item) => {
      merged[item.id] = item;
    });
    const flatRootGoals = (merged[rootId]?.children ?? [])
      .filter((sectionId) => !isStaticSection(sectionId))
      .flatMap((sectionId) => merged[sectionId]?.children ?? []);

    onReorder(
      [{ id: rootId, goalSorting: flatRootGoals }],
      updatedDocs,
      undefined,
      goalParentChanges,
    );
  };

  const { close, position, payload } = useContextMenu();

  const renderItem = useCallback(
    ({ item, itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goalsMap[itemId];
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            rootLink={rootLink}
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea={hideLifeArea}
            showParentGoalLabel={showParentGoalLabel}
            showParentLifeAreaLabel={showParentLifeAreaLabel}
            showParentLabelsForGoals={showParentLabelsForGoals}
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;
        const isStatic = isStaticSection(itemId);

        return (
          <TreeSectionHeader
            item={itemInstance}
            lifeArea={undefined}
            overrideTitle={sectionTitle(itemId)}
            overrideIcon={null}
            disableLink
            useSecondaryTitle={isStatic}
            isFirst={isFirst}
            onAddGoal={
              isStatic
                ? undefined
                : () =>
                    openCreateInList({
                      deadline: getDeadlineQuarterEndDate(itemId),
                    })
            }
          />
        );
      }

      return null;
    },
    [
      treeItems,
      rootLink,
      goalsMap,
      onUpdateProgress,
      hideLifeArea,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
      showParentGoalLabel,
      showParentLifeAreaLabel,
      showParentLabelsForGoals,
      openCreateInList,
      sectionTitle,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={_onReorder}
        dropTypes={droppedTypes}
        dragTypes={dragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
